import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/use-auth";
import { LocationProvider } from "@/contexts/LocationContext";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";
import { SplashScreen as CustomSplashScreen } from "@/components/SplashScreen";
import { initializeOneSignal } from "@/lib/onesignal";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { TierGate } from "@/components/auth/TierGate";
import Index from "./pages/Index";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Visits from "./pages/Visits";
import VisitDetail from "./pages/VisitDetail";
import UnmatchedQueue from "./pages/UnmatchedQueue";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Packages from "./pages/Packages";
import PackageDetail from "./pages/PackageDetail";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import QRScan from "./pages/QRScan";
import QRPrint from "./pages/QRPrint";
import QRRedirect from "./pages/QRRedirect";
import Calendar from "./pages/Calendar";
import Sales from "./pages/Sales";
import NewSale from "./pages/NewSale";
import SaleDetail from "./pages/SaleDetail";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import OnHoldSale from "./pages/OnHoldSale";
import OnHoldReport from "./pages/OnHoldReport";
import OnHoldDetail from "./pages/OnHoldDetail";
import Reports from "./pages/Reports";
import DeadStockDetail from "./pages/DeadStockDetail";
import StockEntry from "./pages/StockEntry";
import InventorySnapshots from "./pages/InventorySnapshots";
import AnnualInventoryReview from "./pages/AnnualInventoryReview";
import AnnualInventoryComparison from "./pages/AnnualInventoryComparison";
import Collections from "./pages/Collections";
import Admin from "./pages/Admin";
import UnknownCustomers from "./pages/UnknownCustomers";
import KnownCustomers from "./pages/KnownCustomers";
import KvkkConsentPage from "./pages/KvkkConsentPage";
import BayiTrades from "./pages/BayiTrades";
import ActivityLog from "./pages/ActivityLog";
import AdminMarsScraper from "./pages/AdminMarsScraper";
import AdminArcelikScraper from "./pages/AdminArcelikScraper";
import EIrsaliye from "./pages/EIrsaliye";
import EIrsaliyeDetail from "./pages/EIrsaliyeDetail";
import NeOdeyecegim from "./pages/NeOdeyecegim";
import Destek from "./pages/Destek";
import CarouselSpin from "./pages/CarouselSpin";
import CarouselAdmin from "./pages/CarouselAdmin";
import NotFound from "./pages/NotFound";
import { isCarouselOnlyApp } from "@/lib/app-variant";

const queryClient = new QueryClient();

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const carouselOnlyMode = isCarouselOnlyApp;

  useEffect(() => {
    const initApp = async () => {
      // Initialize splash screen on native platforms
      if (Capacitor.isNativePlatform()) {
        try {
          // Keep native webview edge-to-edge and hide status bar.
          await StatusBar.setOverlaysWebView({ overlay: true });
          await StatusBar.hide();

          // Show native splash screen
          await SplashScreen.show({
            autoHide: false,
          });

          // Hide native splash screen immediately and show custom one
          setTimeout(async () => {
            await SplashScreen.hide();
          }, 500);
        } catch (error) {
          console.error('Splash screen error:', error);
        }
      }
    };

    initApp();
  }, []);

  useEffect(() => {
    void initializeOneSignal();
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  if (showSplash) {
    return <CustomSplashScreen onComplete={handleSplashComplete} duration={2500} />;
  }

  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LocationProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {carouselOnlyMode ? (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/carousel" element={<ProtectedRoute><TierGate><CarouselSpin /></TierGate></ProtectedRoute>} />
                  <Route path="*" element={<ProtectedRoute><Navigate to="/carousel" replace /></ProtectedRoute>} />
                </>
              ) : (
                <>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/kvkk/:requestId/:token" element={<KvkkConsentPage />} />
                  <Route path="/neodeyecegim" element={<NeOdeyecegim />} />
                  
                  {/* Protected routes with tier gating */}
                  <Route path="/" element={<ProtectedRoute><TierGate><Index /></TierGate></ProtectedRoute>} />
                  <Route path="/customers" element={<ProtectedRoute><TierGate><Customers /></TierGate></ProtectedRoute>} />
                  <Route path="/customers/:id" element={<ProtectedRoute><TierGate><CustomerDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/visits" element={<ProtectedRoute><TierGate><Visits /></TierGate></ProtectedRoute>} />
                  <Route path="/visits/:id" element={<ProtectedRoute><TierGate><VisitDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/packages" element={<ProtectedRoute><TierGate><Packages /></TierGate></ProtectedRoute>} />
                  <Route path="/packages/:id" element={<ProtectedRoute><TierGate><PackageDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/unmatched" element={<ProtectedRoute><TierGate><UnmatchedQueue /></TierGate></ProtectedRoute>} />
                  <Route path="/products" element={<ProtectedRoute><TierGate><Products /></TierGate></ProtectedRoute>} />
                  <Route path="/products/:id" element={<ProtectedRoute><TierGate><ProductDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/qr/product/:productId" element={<ProtectedRoute><TierGate><QRRedirect /></TierGate></ProtectedRoute>} />
                  <Route path="/qr-scan" element={<ProtectedRoute><TierGate><QRScan /></TierGate></ProtectedRoute>} />
                  <Route path="/qr-print" element={<ProtectedRoute><TierGate><QRPrint /></TierGate></ProtectedRoute>} />
                  <Route path="/calendar" element={<ProtectedRoute><TierGate><Calendar /></TierGate></ProtectedRoute>} />
                  <Route path="/sales" element={<ProtectedRoute><TierGate><Sales /></TierGate></ProtectedRoute>} />
                  <Route path="/sales/new" element={<ProtectedRoute><TierGate><NewSale /></TierGate></ProtectedRoute>} />
                  <Route path="/sales/:id" element={<ProtectedRoute><TierGate><SaleDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/collections" element={<ProtectedRoute><TierGate><Collections /></TierGate></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><TierGate><Notifications /></TierGate></ProtectedRoute>} />
                  <Route path="/onhold-sale" element={<ProtectedRoute><TierGate><OnHoldSale /></TierGate></ProtectedRoute>} />
                  <Route path="/onhold-report" element={<ProtectedRoute><TierGate><OnHoldReport /></TierGate></ProtectedRoute>} />
                  <Route path="/onhold-detail/:id" element={<ProtectedRoute><TierGate><OnHoldDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/reports" element={<ProtectedRoute><TierGate><Reports /></TierGate></ProtectedRoute>} />
                  <Route path="/reports/dead-stock" element={<ProtectedRoute><TierGate><DeadStockDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/stock-entry" element={<ProtectedRoute><TierGate><StockEntry /></TierGate></ProtectedRoute>} />
                  <Route path="/annual-inventory-review" element={<ProtectedRoute><TierGate><AnnualInventoryReview /></TierGate></ProtectedRoute>} />
                  <Route path="/annual-inventory-comparison" element={<ProtectedRoute><TierGate><AnnualInventoryComparison /></TierGate></ProtectedRoute>} />
                  <Route path="/inventory-snapshots" element={<ProtectedRoute><TierGate><InventorySnapshots /></TierGate></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><TierGate><Settings /></TierGate></ProtectedRoute>} />
                  <Route path="/destek" element={<ProtectedRoute><TierGate><Destek /></TierGate></ProtectedRoute>} />
                  <Route path="/unknown-customers" element={<ProtectedRoute><TierGate><UnknownCustomers /></TierGate></ProtectedRoute>} />
                  <Route path="/known-customers" element={<ProtectedRoute><TierGate><KnownCustomers /></TierGate></ProtectedRoute>} />
                  <Route path="/bayi-trades" element={<ProtectedRoute><TierGate><BayiTrades /></TierGate></ProtectedRoute>} />
                  <Route path="/activity-log" element={<ProtectedRoute><TierGate><ActivityLog /></TierGate></ProtectedRoute>} />
                  <Route path="/eirsaliye" element={<ProtectedRoute><TierGate><EIrsaliye /></TierGate></ProtectedRoute>} />
                  <Route path="/eirsaliye/:id" element={<ProtectedRoute><TierGate><EIrsaliyeDetail /></TierGate></ProtectedRoute>} />
                  <Route path="/carousel" element={<ProtectedRoute><TierGate><CarouselSpin /></TierGate></ProtectedRoute>} />
                  <Route path="/carousel/admin" element={<ProtectedRoute><TierGate><CarouselAdmin /></TierGate></ProtectedRoute>} />

                  {/* Platform admin routes — NO tier gating */}
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/admin/mars-scraper" element={<ProtectedRoute><AdminMarsScraper /></ProtectedRoute>} />
                  <Route path="/admin/arcelik-scraper" element={<ProtectedRoute><AdminArcelikScraper /></ProtectedRoute>} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
                </>
              )}
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </LocationProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
